/**
 * Client for the token-gated crossborder quote-page proxy routes
 * (apps/gateway/src/routes/crossborder/routes/quote-page-public.routes.ts).
 *
 * The :token path segment is the ONLY credential — no Authorization header, no API key.
 * The gateway resolves tenantId/customerId server-side from the signed link the token
 * points at, so pricing here always matches the customer's real policy.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? window.location.origin;

export interface PayinCountry {
  country: string;
  currency: string;
  label: string;
}

// /payin-countries returns {country, currency} pairs sourced live from Vita's /payins_prices.
// This map is labels-only — currency comes from the API response, not from here.
// Extend here when a new payin country goes live so it gets a display name; an unmapped code
// still works (falls back to the raw code as its own label).
const COUNTRY_DISPLAY: Record<string, string> = {
  CO: '🇨🇴 Colombia',
  MX: '🇲🇽 México',
  AR: '🇦🇷 Argentina',
  CL: '🇨🇱 Chile',
  BO: '🇧🇴 Bolivia',
  BR: '🇧🇷 Brasil',
  US: '🇺🇸 Estados Unidos',
};

function toPayinCountry(entry: { country: string; currency: string }): PayinCountry {
  const iso = entry.country.toUpperCase();
  return { country: iso, currency: entry.currency.toUpperCase(), label: COUNTRY_DISPLAY[iso] ?? iso };
}

// Keyed by destination COUNTRY, not currency — several distinct countries share the same
// USD-currency corridor (Panama, USA, Canada, Hong Kong, China, international SWIFT), so a
// currency-only table can only ever show one of them. Previously keyed by currency, which
// silently collapsed every USD destination into a single "Estados Unidos" label regardless
// of which one was actually selected — confirmed live 2026-07-30, customer report that
// Panama was missing from the destination list entirely. Extend here alongside any new
// corridor added to SwapProviderRegistry's vita_wallet supported_corridors.
const DESTINATION_DISPLAY: Record<string, string> = {
  CO: '🇨🇴 Colombia',
  PE: '🇵🇪 Perú',
  MX: '🇲🇽 México',
  BR: '🇧🇷 Brasil',
  AR: '🇦🇷 Argentina',
  CL: '🇨🇱 Chile',
  BO: '🇧🇴 Bolivia',
  CR: '🇨🇷 Costa Rica',
  DO: '🇩🇴 Rep. Dominicana',
  PY: '🇵🇾 Paraguay',
  UY: '🇺🇾 Uruguay',
  VE: '🇻🇪 Venezuela',
  EU: '🇪🇺 Europa (SEPA)',
  GB: '🇬🇧 Reino Unido',
  AU: '🇦🇺 Australia',
  US: '🇺🇸 Estados Unidos',
  PA: '🇵🇦 Panamá',
  EC: '🇪🇨 Ecuador',
  CA: '🇨🇦 Canadá',
  HK: '🇭🇰 Hong Kong',
  CN: '🇨🇳 China',
  INTL: '🌐 Internacional (SWIFT)',
};

export function destinationLabel(country: string, fallbackCurrency?: string): string {
  return DESTINATION_DISPLAY[country] ?? fallbackCurrency ?? country;
}

// PR-CROSSBORDER-QUOTE-PAGE-RATE-PREVIEW-V1: round, source-currency-native sample amounts
// for the "send more, get a better rate" preview table — shown right after source+
// destination are picked, before the customer has typed any amount. Values are
// deliberately round numbers a customer would actually recognize/type in that currency
// (e.g. 100.000 COP), not a fixed USD tier converted on the fly — picking three fixed USD
// tiers would require an extra estimate call just to learn the FX rate before this table
// could even show approximate local amounts. Same currency set as COUNTRY_DISPLAY (the
// payin countries this page actually offers); an unlisted source currency falls back to
// a plain 10/100/500 (reasonable for most currencies, though not calibrated).
const SAMPLE_AMOUNT_TIERS: Record<string, [number, number, number]> = {
  COP: [40_000, 400_000, 2_000_000],
  MXN: [200, 2_000, 10_000],
  ARS: [10_000, 100_000, 500_000],
  CLP: [10_000, 100_000, 500_000],
  BOB: [70, 700, 3_500],
  BRL: [50, 500, 2_500],
  USD: [10, 100, 500],
};
const DEFAULT_SAMPLE_AMOUNT_TIERS: [number, number, number] = [10, 100, 500];

export function sampleAmountTiersFor(sourceCurrency: string): [number, number, number] {
  return SAMPLE_AMOUNT_TIERS[sourceCurrency.toUpperCase()] ?? DEFAULT_SAMPLE_AMOUNT_TIERS;
}

export interface CrossBorderRoute {
  providerId: string;
  railType: string;
  destinationCountry: string | null;
  destinationCurrency: string;
  payoutMethod: string;
  // Distinguishes a provider-priced alternate payout rail for the same destinationCountry
  // (e.g. 'vita_euusd' — USD payout into Europe, alongside the plain EUR/SEPA route for
  // the same country). null means the provider's default rail for that currency.
  payoutMethodVariant: string | null;
  readiness: string;
}

export interface EstimateResponse {
  quoteStage: string;
  mode: 'sender_pays' | 'recipient_receives';
  senderPays: { amount: string; currency: string };
  recipientReceives: { amount: string; currency: string };
  rate: number;
  fee: { amount: number; currency: string };
  feeWaived: boolean;
  feeLabel: string | null;
  isFirstTransfer?: boolean;
}

export type EstimateMode = 'sender_pays' | 'recipient_receives';

export interface EstimateParams {
  sourceCountry: string;
  sourceCurrency: string;
  destinationCountry: string;
  destinationCurrency: string;
  // Forwarded to the backend as-is when the selected destination is a payout-method
  // variant (e.g. USD-in-EU) rather than the country's default currency — the estimate
  // endpoint already supports this field (EstimateBodySchema), it just wasn't reachable
  // from this page until routes started exposing distinct variant entries.
  payoutMethodVariant?: string | null;
  mode: EstimateMode;
  amount: string;
}

export class QuotePageApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
  }
}

export class QuotePageApi {
  constructor(private readonly token: string) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${API_BASE}/v4/crossborder/quote-page/${this.token}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new QuotePageApiError(res.status, body.error ?? 'UNKNOWN_ERROR', body.message ?? 'Request failed');
    }

    return res.json() as Promise<T>;
  }

  // byProvider maps providerId -> the payin countries THAT PROVIDER accepts (e.g.
  // { vita_wallet: ['CO','MX',...], bridge: ['US'] }) — needed to scope the destination
  // dropdown to only the provider(s) that actually accept the selected payin country. Every
  // full_remittance_rail route's own sourceCurrency is 'USD' regardless of provider (it's a
  // settlement/pricing currency, not the sender's payin country — confirmed live 2026-09-06:
  // filtering /routes by ?source=USD was a no-op, since Vita's own corridors ALSO declare
  // sourceCurrency: 'USD'), so route-level source filtering can never distinguish "which
  // provider serves this payin country" — providerId from this map is the only real signal.
  async fetchPayinCountries(): Promise<{ countries: PayinCountry[]; byProvider: Record<string, string[]> }> {
    const res = await this.request<{ success: boolean; data: { countries: Array<{ country: string; currency: string }>; byProvider?: Record<string, string[]> } }>('/payin-countries');
    return { countries: res.data.countries.map(toPayinCountry), byProvider: res.data.byProvider ?? {} };
  }

  async fetchRoutes(): Promise<{ routes: CrossBorderRoute[] }> {
    const res = await this.request<{ success: boolean; data: { routes: CrossBorderRoute[]; count: number } }>('/routes');
    return res.data;
  }

  async fetchEstimate(params: EstimateParams): Promise<EstimateResponse> {
    const res = await this.request<{ success: boolean; data: EstimateResponse }>('/estimate', {
      method: 'POST',
      body: JSON.stringify({
        source: 'USD',
        sourceCountry: params.sourceCountry,
        sourceCurrency: params.sourceCurrency,
        destinationCountry: params.destinationCountry,
        destinationCurrency: params.destinationCurrency,
        ...(params.payoutMethodVariant ? { payoutMethodVariant: params.payoutMethodVariant } : {}),
        mode: params.mode,
        amount: params.amount,
      }),
    });
    return res.data;
  }

  async submit(values: Record<string, unknown>): Promise<{ success: boolean }> {
    // NOTE: the generic hosted-forms route registered here is
    // POST /v4/public/forms/:token/submissions (plural) — apps/gateway/src/routes/
    // forms/routes/forms-tokens.routes.ts. A sibling /v4/public/forms/:token/submit
    // (singular) exists in forms-public.routes.ts but is NOT registered in this
    // deployment (commented out in forms/index.ts to avoid a route collision) —
    // confirmed live 2026-07-22, returned 404.
    const res = await fetch(`${API_BASE}/v4/public/forms/${this.token}/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new QuotePageApiError(res.status, body.error ?? 'SUBMIT_FAILED', body.message ?? 'Submit failed');
    }

    return res.json() as Promise<{ success: boolean }>;
  }
}
